import { writable } from 'svelte/store';
import { SessionExpiredError } from './apiclient';

/**
 * Creates an action runner for a dialog. The runner wraps the actions of the dialog so
 * that only one of them can run at a time, which matters since several actions send more
 * than one request, and since the actions are triggered both by buttons and by the global
 * key listeners.
 *
 * The returned store holds the handler of the currently running action, or null when the
 * dialog is idle. That lets every button of the dialog disable itself while an action is
 * running, and lets the button of the running action show a spinner.
 *
 * @returns {Object} An object with the busy store and the run function.
 */
export function createActionRunner() {
    const busy = writable(null);
    let current = null;

    /**
     * Wraps an action in a handler that does nothing while another action is running.
     *
     * The wrapped handler never rejects. If the action fails, the error is reported to the
     * user and the dialog becomes idle again so the action can be retried.
     *
     * @param {Function} action The action to wrap. May return a promise.
     * @returns {Function} The handler to use for buttons and key listeners.
     */
    function run(action) {
        const handler = async (...args) => {
            if (current) {
                return;
            }

            current = handler;
            busy.set(handler);

            try {
                await action(...args);
            } catch (err) {
                console.log(err);

                // An expired session takes the user to the login form, so asking them to
                // try the action again would only be misleading.
                if (!(err instanceof SessionExpiredError)) {
                    alert('Åtgärden misslyckades. Försök igen.');
                }
            } finally {
                current = null;
                busy.set(null);
            }
        };

        return handler;
    }

    return { busy, run };
}
